// M06-05 (UI-Audit C1 / GPS Capture) — real device geolocation via the Web
// Geolocation API, replacing manual-only lat/lng entry. Shared by the create
// wizard (site GPS) and the site survey / photo upload flow.
export interface GeoFix {
  readonly lat: number;
  readonly lng: number;
  /** Reported horizontal accuracy in metres (null if the device omits it). */
  readonly accuracyM: number | null;
  /** ISO timestamp of the fix. */
  readonly capturedAt: string;
}

// §29 GPS tampering / accuracy gate — a fix coarser than this is flagged so the
// field user re-captures rather than submitting a low-confidence location.
export const GPS_ACCURACY_WARN_M = 100;

/**
 * Capture the current device location. Rejects with a human-readable Error when
 * geolocation is unavailable, denied, or times out — callers surface the message
 * and may fall back to manual entry.
 */
export function captureGeo(timeoutMs = 15000): Promise<GeoFix> {
  return new Promise<GeoFix>((resolve, reject) => {
    const geo =
      typeof navigator !== 'undefined' ? navigator.geolocation : undefined;
    if (!geo || typeof geo.getCurrentPosition !== 'function') {
      reject(new Error('Geolocation is not available on this device'));
      return;
    }
    geo.getCurrentPosition(
      (pos) =>
        resolve({
          lat: pos.coords.latitude,
          lng: pos.coords.longitude,
          accuracyM: pos.coords.accuracy ?? null,
          capturedAt: new Date(pos.timestamp || Date.now()).toISOString(),
        }),
      (err) => reject(new Error(err?.message || 'Unable to acquire GPS location')),
      { enableHighAccuracy: true, timeout: timeoutMs, maximumAge: 0 },
    );
  });
}

/** True when a fix is trustworthy enough to submit without a tamper warning. */
export function isAccurateFix(fix: GeoFix): boolean {
  return fix.accuracyM == null || fix.accuracyM <= GPS_ACCURACY_WARN_M;
}
